import {
  FilterOperators,
  TrainingCompaniesTraineesQuery,
  useTrainingCompaniesTraineesQuery,
  useMyTraineesQuery,
  useProfessionsMinimalQuery
} from '@typings/graphql'
import { Roles } from '@typings/roles'
import { isRole } from '@utils/permissions'
import React from 'react'

const PAGE_SIZE = 50

export type TrainingCompanies = Exclude<TrainingCompaniesTraineesQuery['trainingCompanies'], null | undefined>[0]

export type TraineeData = Exclude<TrainingCompanies['trainees']['edges'], null | undefined>[0]['node']

export type ProfessionFilter = {
  id: string
  name: string
}

export type DashboardTrainingCompanyProviderContextType = {
  trainees?: TraineeData[]
  notConfirmedCount: number
  trainingYearFilter: number[]
  professionFilter: string[]
  availableTrainingYears: number[]
  availableProfessions: ProfessionFilter[]
  loading?: boolean
  page: number
  pageCount: number
  updatePage: (page: number) => void
  updateTrainingYearFilter: (trainingYears: number[]) => void
  updateProfessionFilter: (profession: string[]) => void
}

const DashboardTrainingCompanyProviderContext = React.createContext<DashboardTrainingCompanyProviderContextType>(
  {} as any
)

export const DashboardTrainingCompanyProvider:React.FC<React.PropsWithChildren> = ({ children }) => {
  const [trainingYearFilter, setTrainingYearFilter] = React.useState<number[]>([])
  const [professionFilter, setProfessionFilter] = React.useState<string[]>([])
  const [page, setPage] = React.useState(0)

  const isTrainingCompany = React.useMemo<boolean>(() => {
    return isRole(Roles.TRAINING_COMPANY_MANAGER)
  }, [])

  const filter = React.useMemo(() => {
    return [
      ...(professionFilter.length ? [{ field: 'professionId', operator: FilterOperators.Equals, value: professionFilter }] : []),
      ...(trainingYearFilter.length ? [{ field: 'trainingYear', operator: FilterOperators.Equals, value: trainingYearFilter }] : [])
    ]
  }, [professionFilter, trainingYearFilter])

  const { data, loading: trainingCompanyLoading } = useTrainingCompaniesTraineesQuery({
    variables: {
      all: false,
      first: PAGE_SIZE,
      skip: page * PAGE_SIZE,
      filter
    },
    fetchPolicy: 'cache-and-network',
    skip: !isTrainingCompany
  })

  const { data: traineesData, loading: traineesLoading } = useMyTraineesQuery({
    fetchPolicy: 'cache-and-network',
    skip: isTrainingCompany
  })

  const { data: professionsData, loading: professionsLoading } = useProfessionsMinimalQuery({
    skip: !isTrainingCompany
  })

  const loading = React.useMemo(() => {
    return trainingCompanyLoading || traineesLoading || professionsLoading
  }, [trainingCompanyLoading, traineesLoading, professionsLoading])

  // Every training company's trainees(...) connection in this response was
  // fetched with the same skip/first, so paging forward always makes
  // progress for all of them - a manager overseeing multiple companies just
  // sees each company contribute up to PAGE_SIZE trainees per page rather
  // than one globally contiguous page.
  const notConfirmedCount = React.useMemo(() => {
    return data?.trainingCompanies?.reduce((sum, trainingCompany) => sum + trainingCompany.traineeStats.notConfirmedCount, 0) ?? 0
  }, [data?.trainingCompanies])

  const availableTrainingYears = React.useMemo(() => {
    const years = new Set<number>()

    data?.trainingCompanies?.forEach((trainingCompany) => {
      trainingCompany.traineeStats.trainingYears.forEach((year) => years.add(year))
    })

    return Array.from(years).sort((a, b) => a - b)
  }, [data?.trainingCompanies])

  const availableProfessions = React.useMemo<ProfessionFilter[]>(() => {
    return professionsData?.professions.map((profession) => ({
      id: profession.id,
      name: profession.texts?.title ?? ''
    })) ?? []
  }, [professionsData])

  const totalCount = React.useMemo(() => {
    if (isTrainingCompany) {
      return data?.trainingCompanies?.reduce((sum, trainingCompany) => sum + trainingCompany.trainees.totalCount, 0) ?? 0
    }

    return traineesData?.me?.trainees?.length ?? 0
  }, [data?.trainingCompanies, traineesData, isTrainingCompany])

  const pageCount = React.useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  }, [totalCount])

  const mappedTrainees = React.useMemo<TraineeData[]>(() => {
    if (isTrainingCompany) {
      return data?.trainingCompanies?.flatMap((trainingCompany) => {
        return trainingCompany.trainees.edges?.map(({ node }) => node) ?? []
      }) ?? []
    }

    return traineesData?.me?.trainees
      ?.filter((student) => student.confirmed && student.profession)
      .map((student) => student) ?? []
  }, [data, traineesData, isTrainingCompany])

  const updateTrainingYearFilter = React.useCallback((value: number[]) => {
    setPage(0)
    setTrainingYearFilter(value)
  }, [])

  const updateProfessionFilter = React.useCallback((value: string[]) => {
    setPage(0)
    setProfessionFilter(value)
  }, [])

  const updatePage = React.useCallback((value: number) => {
    setPage(value)
  }, [])

  const value = React.useMemo(() => ({
    trainingYearFilter,
    trainees: mappedTrainees,
    notConfirmedCount,
    professionFilter,
    loading,
    page,
    pageCount,
    availableTrainingYears,
    availableProfessions,
    updatePage,
    updateTrainingYearFilter,
    updateProfessionFilter
  }), [
    mappedTrainees,
    notConfirmedCount,
    professionFilter,
    availableTrainingYears,
    availableProfessions,
    trainingYearFilter,
    page,
    pageCount,
    loading
  ])

  return (
    <DashboardTrainingCompanyProviderContext.Provider value={value}>
      {children}
    </DashboardTrainingCompanyProviderContext.Provider>
  )
}

export const useDashboardTrainingCompanyContext = () => React.useContext(DashboardTrainingCompanyProviderContext)
